import { deserializeTree, leafOrder, serializeTree, MAX_PANES } from './paneTreeModel';
import { toLaunchSpec } from './terminalProfiles';

/**
 * Saved layouts (pure). A layout stores the SHAPE (tree with slot indices)
 * plus one slot per leaf — `{ profileId | shellKey | connId, cwd, label }` —
 * and a `resume` flag. Never scrollback, env values or tokens; `connId` is a
 * vault item id that Rust resolves at open time, exactly like a new SSH tab.
 */

export function captureLayout(tab, panes, { id, name, rememberCwd = true, resume = true, now = new Date() } = {}) {
  const order = leafOrder(tab.layout);
  const stamp = now.toISOString();
  return {
    id: id || `l_${now.getTime().toString(36)}`,
    name: String(name || '').trim() || 'Layout',
    createdAt: stamp,
    updatedAt: stamp,
    tree: serializeTree(tab.layout),
    slots: order.map((pid) => {
      const p = panes[pid] || {};
      return {
        profileId: p.profileId || null,
        shellKey: p.profileId ? null : p.shellKey || null,
        connId: p.kind === 'ssh' && !p.ai ? p.connId || null : null,
        cwd: rememberCwd ? p.liveCwd || p.cwd || null : null,
        label: p.label || null,
      };
    }),
    resume: !!resume,
  };
}

/**
 * Turn a saved layout into a tree of NEW pane ids plus the pane specs to
 * create. `resume` appends a profile's resume args (e.g. `claude
 * --continue`) — but only to the FIRST slot with that profile + cwd: two
 * `--continue` in the same folder would both resume the same conversation.
 * Returns `{ tree, specs, notices }`; `tree` is null for a corrupt layout.
 */
export function restoreLayout(layout, { profiles = [], newId, defaultCwd }) {
  const ids = [];
  const tree = deserializeTree(layout?.tree, (i) => {
    const pid = newId();
    ids[i] = pid;
    return pid;
  });
  if (!tree) return { tree: null, specs: [], notices: ['corrupt'] };
  const notices = [];
  const resumed = new Set();
  const slots = Array.isArray(layout.slots) ? layout.slots.slice(0, MAX_PANES) : [];
  const specs = ids.map((pid, i) => {
    const slot = slots[i] || {};
    const cwd = slot.cwd || undefined;
    // A slot without a remembered folder starts in the Terminal's default one.
    const plainCwd = cwd || defaultCwd || undefined;
    if (slot.profileId) {
      const profile = profiles.find((p) => p.id === slot.profileId);
      if (!profile) {
        notices.push({ kind: 'missingProfile', label: slot.label || slot.profileId });
        return { id: pid, kind: 'local', label: 'Terminal', cwd: plainCwd };
      }
      const key = `${profile.id}|${(cwd || '').toLowerCase()}`;
      const resume = !!layout.resume && profile.resumeArgs?.length > 0 && !resumed.has(key);
      if (resume) resumed.add(key);
      const spec = toLaunchSpec(profile, { focusedCwd: cwd, defaultCwd, resume });
      // A remembered cwd wins over the profile's mode; "ask" never prompts
      // on restore (the layout already knows where it was).
      return { ...spec, id: pid, cwd: cwd || spec.cwd };
    }
    if (slot.connId) return { id: pid, kind: 'ssh', connId: slot.connId, label: slot.label || 'SSH' };
    return { id: pid, kind: 'local', shellKey: slot.shellKey || undefined, label: slot.label || 'Terminal', cwd: plainCwd };
  });
  return { tree, specs, notices };
}
